/**
 * =============================================================================
 * A+ CONTENT IMAGE GALLERY - Premium Edition
 * BrainyTots Theme - Science-based toys that give joy to little minds
 * =============================================================================
 */

class AplusGallery {
  constructor(container) {
    this.container = container;
    this.items = Array.from(container.querySelectorAll('.bt-aplus-gallery__item'));

    if (this.items.length === 0) return;

    this.settings = {
      enableAnimations: container.dataset.enableAnimations === 'true',
      animationStyle: container.dataset.animationStyle || 'fade_up'
    };

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.observers = [];

    this.init();
  }

  init() {
    // Initialize animations based on style
    if (this.settings.enableAnimations && !this.prefersReducedMotion) {
      switch (this.settings.animationStyle) {
        case 'parallax':
          this.initScrollReveal('fade_in');
          this.initParallax();
          break;
        case 'scale_reveal':
          this.initScrollReveal('scale_reveal');
          break;
        case 'fade_in':
          this.initScrollReveal('fade_in');
          break;
        case 'fade_up':
        default:
          this.initScrollReveal('fade_up');
      }
    }

    this.initLazyLoading();
    this.initTouchFeedback();
  }

  /**
   * Scroll-triggered reveal animations using IntersectionObserver
   */
  initScrollReveal(style) {
    const animationClass = style === 'scale_reveal'
      ? 'bt-aplus-gallery__item--scale-reveal'
      : style === 'fade_in'
        ? 'bt-aplus-gallery__item--fade-in'
        : 'bt-aplus-gallery__item--animate';

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -60px 0px', // Trigger slightly before fully visible
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Small delay to ensure smooth animation start
          requestAnimationFrame(() => {
            entry.target.classList.add('is-visible');
          });
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    this.items.forEach(item => {
      item.classList.add(animationClass);
      observer.observe(item);
    });

    this.observers.push(observer);
  }

  /**
   * Subtle parallax effect on scroll
   */
  initParallax() {
    this.container.classList.add('bt-aplus-gallery--parallax');

    let ticking = false;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      lastScrollY = window.scrollY;

      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Throttled scroll handler
    this.scrollHandler = handleScroll;
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial calculation
    this.updateParallax();
  }

  updateParallax() {
    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;

    this.items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();

      // Only process items that are near the viewport
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) {
        return;
      }

      const itemCenter = rect.top + rect.height / 2;
      const distanceFromCenter = (itemCenter - viewportCenter) / viewportHeight;

      // Subtle offset: max 15px in either direction
      const maxOffset = 15;
      const direction = index % 2 === 0 ? 1 : -1; // Alternate directions
      const offset = distanceFromCenter * maxOffset * direction;

      item.style.setProperty('--parallax-offset', `${offset}px`);
    });
  }

  /**
   * Lazy loading with smooth fade-in
   */
  initLazyLoading() {
    const images = this.container.querySelectorAll('.bt-aplus-gallery__image');

    images.forEach(img => {
      const item = img.closest('.bt-aplus-gallery__item');

      // Check if image is already loaded (cached)
      if (img.complete && img.naturalHeight !== 0) {
        img.classList.add('is-loaded');
        return;
      }

      // Add loading state
      item.classList.add('is-loading');

      img.addEventListener('load', () => {
        item.classList.remove('is-loading');
        img.classList.add('is-loaded');
      }, { once: true });

      img.addEventListener('error', () => {
        item.classList.remove('is-loading');
        item.classList.add('is-error');
        console.warn('A+ Gallery: Failed to load image', img.src);
      }, { once: true });
    });
  }

  /**
   * Touch feedback for mobile devices
   */
  initTouchFeedback() {
    // Only for touch devices
    if (!('ontouchstart' in window)) return;

    this.items.forEach(item => {
      let touchTimeout;

      item.addEventListener('touchstart', () => {
        // Small delay to differentiate tap from scroll
        touchTimeout = setTimeout(() => {
          item.classList.add('is-pressed');
        }, 50);
      }, { passive: true });

      item.addEventListener('touchend', () => {
        clearTimeout(touchTimeout);
        item.classList.remove('is-pressed');
      }, { passive: true });

      item.addEventListener('touchcancel', () => {
        clearTimeout(touchTimeout);
        item.classList.remove('is-pressed');
      }, { passive: true });

      item.addEventListener('touchmove', () => {
        clearTimeout(touchTimeout);
        item.classList.remove('is-pressed');
      }, { passive: true });
    });
  }

  /**
   * Cleanup method for removing event listeners
   */
  destroy() {
    // Remove scroll handler
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    // Disconnect all observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];

    // Remove initialized flag
    delete this.container.dataset.initialized;
  }
}

/**
 * Initialize all A+ galleries on the page
 */
function initAplusGalleries() {
  document.querySelectorAll('.bt-aplus-gallery').forEach(gallery => {
    if (!gallery.dataset.initialized) {
      new AplusGallery(gallery);
      gallery.dataset.initialized = 'true';
    }
  });
}

/**
 * Reinitialize a specific gallery (useful for theme editor)
 */
function reinitAplusGallery(container) {
  // Destroy existing instance if any
  if (container.aplusGalleryInstance) {
    container.aplusGalleryInstance.destroy();
  }

  delete container.dataset.initialized;
  const instance = new AplusGallery(container);
  container.aplusGalleryInstance = instance;
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAplusGalleries);
} else {
  initAplusGalleries();
}

// Shopify theme editor support
document.addEventListener('shopify:section:load', (event) => {
  const gallery = event.target.querySelector('.bt-aplus-gallery');
  if (gallery) {
    reinitAplusGallery(gallery);
  }
});

document.addEventListener('shopify:section:reorder', initAplusGalleries);

document.addEventListener('shopify:block:select', (event) => {
  const gallery = event.target.closest('.bt-aplus-gallery');
  if (gallery) {
    reinitAplusGallery(gallery);
  }
});

// Export for external use
window.AplusGallery = AplusGallery;
window.initAplusGalleries = initAplusGalleries;
window.reinitAplusGallery = reinitAplusGallery;
